import React from 'react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@frontend/shadcn/components/ui/accordion';
import { CircleInfoIcon, DollarSignIcon, GiftCardIcon, TagsIcon } from '@revfluence/fresh-icons/solid/esm';
import Tag from '@frontend/app/refresh-components/Tag';
import { Tooltip, TooltipContent, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { UserIcon } from '@revfluence/fresh-icons/solid';
import { RadioGroup, RadioGroupItem } from '@/shadcn/components/ui/radio-group';
import { BoostCardData } from '../../utils';

interface IProps {
  selectedId: number | null;
  setSelectedId: (id: number | null) => void;
  boostCardsData: BoostCardData[];
}

export const getBoostIconType = (type: string): React.ReactNode => {
  switch (type) {
    case 'cash':
      return (
        <div className="p-2 rounded-lg bg-teal-4 w-6 h-6 flex items-center justify-center">
          <DollarSignIcon className="w-3 h-3 text-grey-0" />
        </div>
      );
    case 'discount':
      return (
        <div className="bg-blue-4 p-2 rounded-lg w-6 h-6 flex items-center justify-center">
          <TagsIcon className="w-3 h-3 text-grey-0" />
        </div>
      );
    case 'gift':
      return (
        <div className="bg-pink-4 p-2 rounded-lg w-6 h-6 flex items-center justify-center">
          <GiftCardIcon className="w-3 h-3 text-grey-0" />
        </div>
      );
    default:
      return null;
  }
};

export const getBoostTagColor = (type: string): string => {
  switch (type) {
    case 'cash':
      return 'bg-teal-2';
    case 'discount':
      return 'bg-blue-2';
    case 'gift':
      return 'bg-magenta-2';
    default:
      return 'bg-grey-2';
  }
};

export const BoostCards: React.FC<IProps> = ({ selectedId, setSelectedId, boostCardsData }) => {
  const handleRadioChange = (value: string) => {
    const id = Number(value);
    setSelectedId(id);
  };

  if (boostCardsData.length === 0) {
    return (
      <div className="p-4 flex flex-col gap-2">
        <p className="text-sm font-normal text-primary m-0">Select the boost you want to approve.</p>
        <div className="text-center py-8 text-grey-5">
          <p>No pending boosts available for approval</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col gap-2">
      <p className="text-sm font-normal text-primary m-0">Select the boost you want to approve.</p>
      <RadioGroup
        value={selectedId?.toString() ?? ''}
        onValueChange={handleRadioChange}
        className="flex flex-col"
        aria-label="Select boost to approve"
      >
        {boostCardsData.map((boostCard) => (
          <Accordion key={boostCard.id} type="single" collapsible className="w-full">
            <AccordionItem value="boost-settings" className="pt-2 pb-1 px-3 border border-grey-2 rounded-xl">
              <AccordionTrigger className="p-0 flex items-center hover:no-underline gap-2">
                <div className="flex gap-2 items-center flex-1">
                  <RadioGroupItem value={boostCard.id.toString()} />
                  {getBoostIconType(boostCard.boostType)}
                  <p className="m-0 text-sm font-medium">{boostCard.label}</p>
                  <Tooltip>
                    <TooltipTrigger>
                      <CircleInfoIcon className="cursor-pointer text-grey-3" fontSize={12} />
                    </TooltipTrigger>
                    <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
                      {boostCard.label}
                    </TooltipContent>
                  </Tooltip>
                </div>
                <p className="text-sm font-normal text-grey-5 m-0">{boostCard.eligibleMembersCount}/{boostCard.totalMemberCount}</p>
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-2 mt-2 pb-0">
                <div className="flex gap-2 items-center justify-between">
                  <p className="text-sm font-normal text-grey-5 m-0">Reward Type</p>
                  <Tag label={boostCard.amount} bgColor={getBoostTagColor(boostCard.boostType)} textColor="text-primary" />
                </div>
                <div className="flex gap-2 items-center justify-between">
                  <p className="text-sm font-normal text-grey-5 m-0">Eligible Members</p>
                  <p className="text-sm font-medium text-primary m-0">
                    {boostCard.eligibleMembersCount}
                  </p>
                </div>
                <div className="flex gap-2 items-center justify-between">
                  <p className="text-sm font-normal text-grey-5 m-0">Pending Approvals
                    <Tooltip>
                      <TooltipTrigger>
                        <CircleInfoIcon className="cursor-pointer text-grey-3 ml-2" fontSize={12} />
                      </TooltipTrigger>
                      <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
                        The number of individual reward claims pending approval. A single creator may have multiple rewards unlocked
                      </TooltipContent>
                    </Tooltip>
                  </p>
                  <p className="text-sm font-medium text-primary m-0">
                    {boostCard.pendingBoostRewards.length}
                  </p>
                </div>
                <div className="flex gap-2 items-center justify-between">
                  <p className="text-sm font-normal text-grey-5 m-0">Total Amount Invested</p>
                  <p className="text-sm font-medium text-primary m-0">
                    {boostCard.totalAmount ? `$${boostCard.totalAmount.toLocaleString()}` : '-'}
                  </p>
                </div>
                <div className="border-b" />
                <div className="flex gap-2 items-center">
                  <UserIcon className="w-3 h-3 text-primary" />
                  <p className="text-sm font-normal text-grey-6 m-0">
                    {boostCard.eligibleMembersCount} Creators will receive {boostCard.amount}
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ))}
      </RadioGroup>
    </div>
  );
};
